// src/components/core/entity/EntityChannelsCard/ContactsChannelModal/Step1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Form, Segmented, Flex, theme } from 'antd';
import {
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  ShareAltOutlined,
  EllipsisOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleWebsite from 'components/SimpleComponents/SimpleWebsite';
import SimpleEmail from 'components/SimpleComponents/SimpleEmail';
import SimplePhone from 'components/SimpleComponents/SimplePhone';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSwitch from 'components/SimpleComponents/SimpleSwitch';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
// Order: 1. Phone, 2. Email, 3. Website, 4. Social Network, 5. Other
const CHANNEL_TYPES = [
  { value: 'phone', label: 'phone', icon: <PhoneOutlined /> },
  { value: 'email', label: 'email', icon: <MailOutlined /> },
  { value: 'website', label: 'website', icon: <GlobalOutlined /> },
  { value: 'social', label: 'social_network', icon: <ShareAltOutlined /> },
  { value: 'other', label: 'other', icon: <EllipsisOutlined /> },
];

const SOCIAL_NETWORKS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
];

// *******************************************************************************
// SEGMENT ITEM COMPONENT
// *******************************************************************************
const SegmentItem = ({ icon, label, isSelected, token }) => (
  <Flex vertical align="center" gap={4} style={{ padding: '8px 4px' }}>
    <span
      style={{
        fontSize: 20,
        color: isSelected ? token.colorPrimary : token.colorTextSecondary,
      }}
    >
      {icon}
    </span>
    <span
      style={{
        fontSize: 12,
        color: isSelected ? token.colorPrimary : token.colorTextSecondary,
        fontWeight: isSelected ? 600 : 400,
      }}
    >
      {translateX(label)}
    </span>
  </Flex>
);

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const ContactChannelStepContent = () => {
  const { form, goFocus } = useSimpleFormContext();
  const { token } = theme.useToken();

  const channelType = Form.useWatch('channelType', form);

  // Auto-focus the appropriate field when step mounts or channel type changes
  useEffect(() => {
    const timer = setTimeout(() => {
      // All fields use value.value structure, except social which focuses on select first
      const focusField = channelType === 'social' ? 'socialNetwork' : 'value.value';
      goFocus?.(focusField, true);
    }, 150);
    return () => clearTimeout(timer);
  }, [goFocus, channelType]);

  // Build segment options with system colors
  const segmentOptions = CHANNEL_TYPES.map(type => ({
    value: type.value,
    label: (
      <SegmentItem
        icon={type.icon}
        label={type.label}
        isSelected={channelType === type.value}
        token={token}
      />
    ),
  }));

  return (
    <>
      {/* Hidden field to preserve contact ID for editing */}
      <Form.Item name="_contactId" hidden>
        <input type="hidden" />
      </Form.Item>

      {/* Channel Type Segment */}
      <Form.Item name="channelType" style={{ marginBottom: 24 }}>
        <Segmented options={segmentOptions} block style={{ padding: 4 }} />
      </Form.Item>

      {/* Dynamic Fields based on Channel Type */}
      {/* All fields use ['value', 'value'] structure for consistent form handling */}
      {channelType === 'website' && (
        <SimpleWebsite
          name={['value', 'value']}
          label="website"
          placeholder="www.example.com"
          required
        />
      )}

      {channelType === 'email' && (
        <SimpleEmail
          name={['value', 'value']}
          label="email"
          placeholder="contact@example.com"
          required
        />
      )}

      {channelType === 'phone' && (
        <SimplePhone name={['value', 'value']} label="phone" international required />
      )}

      {channelType === 'social' && (
        <>
          <SimpleSelect
            name="socialNetwork"
            label="social_network"
            options={SOCIAL_NETWORKS}
            useCustomLabel
            required
          />
          <SimpleWebsite
            name={['value', 'value']}
            label="profile_url"
            placeholder="https://instagram.com/your_profile"
            required
          />
        </>
      )}

      {channelType === 'other' && (
        <SimpleInput
          name={['value', 'value']}
          label="reference"
          placeholder="other_channel_placeholder"
          maxLength={200}
          required
        />
      )}

      {/* Note Field */}
      <SimpleTextArea
        name="note"
        label="note"
        rows={2}
        maxLength={200}
        placeholder="note_placeholder"
      />

      {/* Primary Contact Switch */}
      {/*<SimpleSwitch name="isPrimary" label="favorite" showLabel />*/}
    </>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const contactChannelStep = () => ({
  stepTitle: 'channel_data',
  stepTooltip: 'configure_contact_channel',
  content: () => <ContactChannelStepContent />,
  // All fields use value.value structure (name={['value', 'value']})
  validationFields: ['channelType', 'value.value'],
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default contactChannelStep;
